import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { Logo } from '../components/Logo'
import { Footer } from '../components/Footer'

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  
  const { login, loginWithGoogle } = useAuthStore()

  // * Check for errors in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const errorParam = urlParams.get('error')
    if (errorParam === 'oauth_failed') {
      setError('Échec de la connexion Google. Veuillez réessayer.')
    } else if (errorParam === 'session_expired') {
      setError('Votre session a expiré. Veuillez vous reconnecter.')
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      await login(email, password)
    } catch (error) {
      setError('Identifiants invalides')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setIsLoading(true)
    setError('')
    try {
      await loginWithGoogle()
    } catch (error: any) {
      // * Display specific error message if available
      const errorMessage = error?.message || 'Erreur lors de la connexion Google'
      setError(errorMessage)
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col gradient-bg layout-container">
      {/* Main content */}
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full space-y-8 page-content">
          {/* Logo and title */}
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <Logo className="w-16 h-16" />
                <div className="absolute inset-0 rounded-full blur-2xl opacity-20" style={{ backgroundColor: 'var(--color-robot-blue)' }} />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-3 tab_title">
              AgentTeamForge Studio
            </h1>
            <p className="text-sm max-w-sm mx-auto text-secondary">
              Créez et gérez vos agents conversationnels intelligents avec une interface
              intuitive et puissante.
            </p>
          </div>

          {/* Login form */}
          <div className="card">
            <h2 className="text-2xl font-bold mb-2 text-center text-primary">
              Connexion
            </h2>
            <p className="text-sm text-center mb-6 text-secondary">
              Connectez-vous pour accéder à votre espace
            </p>
            
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1 form-label">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="form-input"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-1 form-label">
                  Mot de passe
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="form-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {error && (
                <div className="px-4 py-3 rounded-lg text-sm alert-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="button button--primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Connexion...' : 'Se connecter'}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full divider" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 divider-label">Ou</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="button button--secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuer avec Google
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  )
}
